import { lazy, Suspense, useEffect, useRef, useState } from "react";
import { PRODUCTS, type Product, type ProductChipTone } from "@/data/products";
import { useLoopingCarousel } from "@/hooks/useLoopingCarousel";

// HeroOrbWebGL and Drawer are lazy — neither blocks the critical path.
// HeroOrbWebGL (ogl/WebGL) is also skipped entirely on mobile via
// the isDesktop state so the ~13 KB gzip ogl chunk is never fetched
// on small screens.
const Orb = lazy(() => import("@/components/HeroOrbWebGL"));
// Drawer (Sheet from @radix-ui + lucide icons) only renders on card
// click, so it is safe to lazy-load — removes radix + icons chunks
// from the initial modulepreload list.
const Drawer = lazy(() => import("@/components/CapabilityCarouselDrawer"));

// Inline SVGs replace lucide-react imports on the critical path.
// These four are always visible (nav buttons + card arrow), so they
// must load with the carousel. Lucide itself stays in the lazy Drawer.
const IcoArrowLeft = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M19 12H5M12 5l-7 7 7 7" />
  </svg>
);
const IcoArrowRight = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M5 12h14M12 5l7 7-7 7" />
  </svg>
);
const IcoArrowUpRight = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M7 17L17 7M17 7H7M17 7v10" />
  </svg>
);

/**
 * Product carousel. Each tile is a promotional card for a Mane product
 * surface. Click opens a rich drawer with what's inside, demo flows,
 * metrics, and a link to the dedicated lander if one exists.
 */

// Renders headline strings that may contain inline <em> for serif italic.
const RichText = ({ html, className }: { html: string; className?: string }) => (
  <span className={className} dangerouslySetInnerHTML={{ __html: html }} />
);

const chipClassFor = (tone: ProductChipTone) => {
  switch (tone) {
    case "security":
      return "bg-black/45 text-white";
    case "connector":
      return "bg-black/45 text-white";
    case "creative":
      return "bg-black/45 text-white";
    default:
      return "bg-black/45 text-white";
  }
};

const Card = ({
  product,
  onOpen,
  isActive,
  carouselInView,
}: {
  product: Product;
  onOpen: () => void;
  isActive: boolean;
  /** True once the carousel section has scrolled into the viewport. Gates
   *  video preload + playback so the 852 KB MP4 never loads during the LCP
   *  window — the carousel pre-mounts before scroll, so without this gate
   *  the active card would immediately set preload="auto" + play(). */
  carouselInView: boolean;
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);

  // Only mount the <video> element once this card has been active at least
  // once. On first render the carousel shows one active card, so only
  // 3 (out of 30) <video> elements exist in the DOM. As the user advances,
  // each newly-active card gets its video mounted lazily. Cards that were
  // already active keep their video elements to avoid remount churn.
  const [everActive, setEverActive] = useState(isActive);
  useEffect(() => {
    if (isActive && !everActive) setEverActive(true);
  }, [isActive, everActive]);

  // Playback effect — also depends on everActive so it re-fires right after
  // the video element is first mounted. carouselInView gates all preload /
  // play calls: the carousel pre-mounts ~250 px before the viewport (down
  // from 800 px), so the first inView=true fires only once the user has
  // actually scrolled to the section. Without this gate, the 852 KB MP4
  // would start downloading during the LCP window.
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    if (isActive && carouselInView) {
      if (v.preload !== "auto") v.preload = "auto";
      try { v.currentTime = 0; } catch { /* DOMException silenced */ }
      v.play().catch(() => { /* autoplay policy silenced */ });
    } else {
      v.pause();
      try { v.currentTime = 0; } catch { /* DOMException silenced */ }
    }
  }, [isActive, everActive, carouselInView]);
  return (
  <button
    type="button"
    onClick={onOpen}
    className="group relative snap-start shrink-0 w-[calc(100vw-32px)] max-w-none aspect-video sm:w-[min(78vw,620px)] md:w-[min(74vw,720px)] lg:w-[min(70vw,860px)] xl:w-[960px] rounded-[6px] sm:rounded-[8px] overflow-hidden text-left transition-[transform,opacity] duration-500 ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
    style={{
      boxShadow:
        "0 0 0 1px rgba(0,0,0,0.08), inset 0 1px 0 rgba(255,255,255,0.14), inset 0 0 0 1px rgba(255,255,255,0.05)",
    }}
    aria-label={`Open ${product.name}`}
  >
    {(() => {
      const posterJpg = product.videoSrc
        ? product.videoSrc.replace("/product-videos/", "/product-videos/posters/").replace(/\.mp4$/, ".jpg")
        : product.image;
      const posterAvif = product.videoSrc
        ? product.videoSrc.replace("/product-videos/", "/product-videos/posters/").replace(/\.mp4$/, ".avif")
        : null;
      return (
        <>
          {/* Use <picture> with AVIF source so browsers that support AVIF (97%+)
              fetch the ~28-42 KB version instead of the 68-101 KB JPG fallback. */}
          <picture>
            {posterAvif && <source srcSet={posterAvif} type="image/avif" />}
            <img
              src={posterJpg}
              alt=""
              loading="lazy"
              decoding="async"
              className="absolute inset-0 w-full h-full object-cover"
            />
          </picture>
          {product.videoSrc && everActive && (
            <video
              ref={videoRef}
              src={product.videoSrc}
              // Prefer the AVIF poster: it's the SAME URL the <picture> above
              // already paints, so the browser dedupes to one ~29 KB fetch
              // instead of additionally pulling the 68-101 KB JPG here. The
              // ~3% of browsers without AVIF fall through to the JPG that the
              // <picture> fallback below still loads, so nothing breaks.
              poster={posterAvif || posterJpg}
              muted
              loop
              playsInline
              preload="none"
              className="absolute inset-0 w-full h-full object-cover"
            />
          )}
        </>
      );
    })()}
    <div
      aria-hidden
      className="absolute inset-x-0 bottom-0 h-[60%]"
      style={{
        background:
          "linear-gradient(180deg, rgba(10,8,6,0) 0%, rgba(10,8,6,0.55) 55%, rgba(10,8,6,0.95) 100%)",
      }}
    />

    <div className="absolute top-3.5 left-3.5 flex items-center gap-1.5">
      <span
        className={`inline-flex items-center px-2 py-0.5 rounded-full font-mono text-[10px] tracking-[0.16em] uppercase ${chipClassFor(product.chip.tone)}`}
        style={{ boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.18)" }}
      >
        {product.chip.label}
      </span>
      {product.badge && (
        <span
          className="inline-flex items-center px-2 py-0.5 rounded-full font-mono text-[10px] tracking-[0.16em] uppercase text-foreground"
          style={{ background: "#fad657" }}
        >
          {product.badge}
        </span>
      )}
    </div>

    <span
      aria-hidden
      className="absolute top-3.5 right-3.5 h-8 w-8 rounded-full bg-black/45 flex items-center justify-center transition-all duration-300 group-hover:bg-accent group-hover:text-foreground"
      style={{ boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.18)" }}
    >
      <IcoArrowUpRight />
    </span>

    <div className="absolute left-5 right-5 bottom-5">
      <div className="mb-2 font-mono text-[10.5px] tracking-[0.22em] uppercase text-white/75">
        {product.name}
      </div>
      <RichText
        html={product.headline}
        className="block text-white text-[20px] sm:text-[22px] leading-[1.12] font-bold tracking-[-0.02em] [text-shadow:0_1px_2px_rgba(0,0,0,0.45)]"
      />
      <p className="hidden sm:block mt-2 text-[13px] leading-[1.45] text-white/85 [text-shadow:0_1px_2px_rgba(0,0,0,0.35)]">
        {product.tagline}
      </p>
    </div>
  </button>
  );
};

const AUTO_MS = 6500;

const CapabilityCarousel = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [inView, setInView] = useState(false);
  // Only render the WebGL orb on desktop to avoid fetching the ~13 KB gzip
  // ogl chunk on mobile. Initialises on first paint (SSR-safe: starts false).
  const [isDesktop, setIsDesktop] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);
  const active = openIndex !== null ? PRODUCTS[openIndex] : null;

  const reduceMotion =
    typeof window !== "undefined" &&
    window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

  const {
    viewportRef,
    trackRef,
    setPaused,
    tick,
    activeIndex,
    autoActive,
    advance,
    handleTransitionEnd,
    trackStyle,
  } = useLoopingCarousel({
    itemCount: PRODUCTS.length,
    autoMs: AUTO_MS,
    inView: inView && openIndex === null,
    reduceMotion,
  });

  useEffect(() => {
    const el = viewportRef.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver(
      ([entry]) => setInView(entry.isIntersecting),
      { threshold: 0.2 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [viewportRef]);

  // Detect desktop on mount (after hydration). Using matchMedia is more
  // reliable than window.innerWidth for foldable / landscape-phone cases.
  useEffect(() => {
    setIsDesktop(window.matchMedia("(min-width: 769px)").matches);
  }, []);

  return (
    <section
      ref={sectionRef}
      aria-labelledby="capability-carousel"
      className="relative isolate py-12 sm:py-16 bg-background overflow-hidden"
    >
      {/* Energy orb backdrop — only on desktop (skips ogl chunk on mobile). */}
      {isDesktop && (
        <div
          aria-hidden
          className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 -z-10 w-[140vw] max-w-[900px] aspect-square sm:w-[90vw] sm:max-w-[1100px] lg:w-[78vw] lg:max-w-[1300px] opacity-25 sm:opacity-30 dark:opacity-40"
          style={{ pointerEvents: "none" }}
        >
          <Suspense fallback={null}>
            <Orb fillContainer hue={0} hueCycleSpeed={20} hoverIntensity={0.45} />
          </Suspense>
        </div>
      )}

      <div className="relative z-10 max-w-content mx-auto px-5 sm:px-8">
        {/* CSS-only fade-up for the heading — framer-motion stays off the
            critical path. Same 18px lift / 0.6s ease-out as the Reveal
            component used elsewhere, but implemented via a Tailwind
            keyframe so no extra JS chunk is needed here. */}
        <div className="mb-10 sm:mb-12 mx-auto max-w-3xl text-center animate-[fadeUp_0.6s_cubic-bezier(0.22,1,0.36,1)_both]">
          <h2 id="capability-carousel" className="h-display max-w-[22ch] mx-auto">
            The agent-native way to get <em>ambitious work</em> done.
          </h2>
        </div>
      </div>

      <div
        className="relative z-10 mx-auto w-full max-w-[1400px]"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        onTouchStart={() => setPaused(true)}
      >
        <div
          ref={viewportRef}
          className="overflow-hidden pt-8 pb-8"
          style={{ touchAction: "pan-y" }}
          onTouchStart={(e) => {
            (e.currentTarget as HTMLDivElement & { _sx?: number; _sy?: number })._sx = e.touches[0].clientX;
            (e.currentTarget as HTMLDivElement & { _sx?: number; _sy?: number })._sy = e.touches[0].clientY;
          }}
          onTouchEnd={(e) => {
            type SwipeDiv = HTMLDivElement & { _sx?: number; _sy?: number };
            const sx = (e.currentTarget as SwipeDiv)._sx;
            const sy = (e.currentTarget as SwipeDiv)._sy;
            if (sx == null) return;
            const dx = e.changedTouches[0].clientX - sx;
            const dy = e.changedTouches[0].clientY - sy;
            if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) {
              advance(dx < 0 ? 1 : -1);
            }
          }}
        >

          <div
            ref={trackRef}
            className="flex gap-3 sm:gap-6 pl-4 pr-4 sm:pl-[calc(50%-min(39vw,310px))] sm:pr-[calc(50%-min(39vw,310px))] md:pl-[calc(50%-min(37vw,360px))] md:pr-[calc(50%-min(37vw,360px))] lg:pl-[calc(50%-min(35vw,430px))] lg:pr-[calc(50%-min(35vw,430px))] xl:pl-[calc(50%-480px)] xl:pr-[calc(50%-480px)]"
            style={trackStyle}
            onTransitionEnd={handleTransitionEnd}
          >
            {[...PRODUCTS, ...PRODUCTS, ...PRODUCTS].map((p, i) => {
              const realIndex = i % PRODUCTS.length;
              const isFocus = realIndex === activeIndex;
              return (
                <div
                  key={`${p.id}-${i}`}
                  className={`shrink-0 transition-[opacity,transform] duration-500 ease-out origin-center ${
                    isFocus ? "scale-100 sm:scale-110" : "scale-95 sm:scale-[0.82]"
                  }`}
                  style={{
                    opacity: isFocus ? 1 : 0.4,
                  }}
                  data-loop-item
                >
                  <Card product={p} onOpen={() => setOpenIndex(realIndex)} isActive={isFocus} carouselInView={inView} />
                </div>
              );
            })}
          </div>
        </div>

        <div
          aria-hidden
          className="pointer-events-none absolute top-0 right-0 h-full w-16 hidden sm:block"
          style={{
            background:
              "linear-gradient(to right, transparent, hsl(var(--background)))",
          }}
        />

        {/* Desktop side arrows */}
        <button
          type="button"
          onClick={() => advance(-1)}
          aria-label="Previous"
          className="hidden sm:flex absolute top-1/2 -translate-y-1/2 left-4 lg:left-8 z-20 h-11 w-11 rounded-full items-center justify-center text-white/95 transition-all hover:scale-105 active:scale-95"
          style={{
            background: "rgba(10,8,6,0.42)",
            boxShadow:
              "inset 0 0 0 1px rgba(255,255,255,0.18), 0 8px 24px -8px rgba(0,0,0,0.45)",
          }}
        >
          <IcoArrowLeft />
        </button>
        <button
          type="button"
          onClick={() => advance(1)}
          aria-label="Next"
          className="hidden sm:flex absolute top-1/2 -translate-y-1/2 right-4 lg:right-8 z-20 h-11 w-11 rounded-full items-center justify-center text-white/95 transition-all hover:scale-105 active:scale-95"
          style={{
            background: "rgba(10,8,6,0.42)",
            boxShadow:
              "inset 0 0 0 1px rgba(255,255,255,0.18), 0 8px 24px -8px rgba(0,0,0,0.45)",
          }}
        >
          <IcoArrowRight />
        </button>
      </div>

      <style>{`
        @keyframes capProgress { from { transform: scaleX(0); } to { transform: scaleX(1); } }
        @keyframes capRingProgress { from { stroke-dashoffset: 56.5; } to { stroke-dashoffset: 0; } }
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(18px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      {/* Mobile pagination: arrows + Higgsfield-style dots */}
      <div className="sm:hidden mt-5 flex items-center justify-center gap-3">
        <button
          type="button"
          onClick={() => advance(-1)}
          aria-label="Previous"
          className="h-9 w-9 shrink-0 rounded-full bg-card flex items-center justify-center active:scale-95 transition-transform"
          style={{ boxShadow: "0 0 0 1px hsl(var(--foreground) / 0.1)" }}
        >
          <IcoArrowLeft />
        </button>
        <div className="flex items-center justify-center gap-2.5">
        {PRODUCTS.map((p, index) => {
          const isActive = index === activeIndex;
          return (
            <button
              key={p.id}
              type="button"
              onClick={() => {
                const diff = index - activeIndex;
                const dir: 1 | -1 = diff >= 0 ? 1 : -1;
                for (let i = 0; i < Math.abs(diff); i++) advance(dir);
              }}
              aria-label={`Go to ${p.name}`}
              className="relative flex items-center justify-center"
              style={{ width: 18, height: 18 }}
            >
              <span
                className="block rounded-full transition-all duration-300"
                style={{
                  width: isActive ? 10 : 6,
                  height: isActive ? 10 : 6,
                  background: isActive
                    ? "hsl(var(--foreground))"
                    : "hsl(var(--foreground) / 0.25)",
                }}
              />
              {isActive && autoActive && (
                <svg
                  key={tick}
                  className="absolute inset-0 -rotate-90"
                  width="18"
                  height="18"
                  viewBox="0 0 18 18"
                  aria-hidden
                >
                  <circle
                    cx="9"
                    cy="9"
                    r="8"
                    fill="none"
                    stroke="hsl(var(--foreground))"
                    strokeWidth="1.2"
                    strokeDasharray="50.3"
                    strokeDashoffset="50.3"
                    style={{
                      animation: `capRingProgress ${AUTO_MS}ms linear forwards`,
                    }}
                  />
                </svg>
              )}
            </button>
          );
        })}
        </div>
        <button
          type="button"
          onClick={() => advance(1)}
          aria-label="Next"
          className="h-9 w-9 shrink-0 rounded-full bg-card flex items-center justify-center active:scale-95 transition-transform"
          style={{ boxShadow: "0 0 0 1px hsl(var(--foreground) / 0.1)" }}
        >
          <IcoArrowRight />
        </button>
      </div>

      {/* Desktop pagination: circle dots (matches mobile) */}
      <div className="hidden sm:flex max-w-content mx-auto px-5 sm:px-8 mt-6 items-center justify-center gap-3">
        <p className="font-mono text-[10.5px] uppercase tracking-[0.22em] text-muted-foreground whitespace-nowrap tabular-nums mr-2">
          {String(activeIndex + 1).padStart(2, "0")}{" "}
          <span className="opacity-45">/</span>{" "}
          {String(PRODUCTS.length).padStart(2, "0")}
        </p>
        <div className="flex items-center justify-center gap-2.5">
          {PRODUCTS.map((p, index) => {
            const isActive = index === activeIndex;
            return (
              <button
                key={p.id}
                type="button"
                onClick={() => {
                  const diff = index - activeIndex;
                  const dir: 1 | -1 = diff >= 0 ? 1 : -1;
                  for (let i = 0; i < Math.abs(diff); i++) advance(dir);
                }}
                aria-label={`Go to ${p.name}`}
                className="relative flex items-center justify-center"
                style={{ width: 18, height: 18 }}
              >
                <span
                  className="block rounded-full transition-all duration-300"
                  style={{
                    width: isActive ? 10 : 6,
                    height: isActive ? 10 : 6,
                    background: isActive
                      ? "hsl(var(--foreground))"
                      : "hsl(var(--foreground) / 0.25)",
                  }}
                />
                {isActive && autoActive && (
                  <svg
                    key={tick}
                    className="absolute inset-0 -rotate-90"
                    width="18"
                    height="18"
                    viewBox="0 0 18 18"
                    aria-hidden
                  >
                    <circle
                      cx="9"
                      cy="9"
                      r="8"
                      fill="none"
                      stroke="hsl(var(--foreground))"
                      strokeWidth="1.2"
                      strokeDasharray="50.3"
                      strokeDashoffset="50.3"
                      style={{
                        animation: `capRingProgress ${AUTO_MS}ms linear forwards`,
                      }}
                    />
                  </svg>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Drawer lazy-loads on first open — Sheet + lucide icons stay out
          of the initial modulepreload. Suspense with null fallback so
          there's no visual flash while the chunk resolves. */}
      <Suspense fallback={null}>
        <Drawer
          product={active}
          open={openIndex !== null}
          onOpenChange={(v) => !v && setOpenIndex(null)}
        />
      </Suspense>
    </section>
  );
};

export default CapabilityCarousel;
